/**
 * Names this caller in the configuration's change history. Every drawer save and every agent CLI
 * write authenticates as the same tenant user, so without it two concurrent writers are
 * indistinguishable in the audit trail.
 */
const CHANGE_SOURCE_HEADER = 'X-Skynet-Change-Source';
const changeSourceConfig = { headers: { [CHANGE_SOURCE_HEADER]: 'AdminPanel' } };

export class TpoSignupConfigurationApi {
  #http;

  constructor(http) {
    this.#http = http;
  }

  async getTpoSignupConfigurations(config) {
    return this.#http.get('/api/tpo-signup-configurations', config);
  }

  async getTpoSignupConfiguration({ id }) {
    return this.#http.get(`/api/tpo-signup-configurations/${id}`);
  }

  async evaluateDecisioning({ id, ...body }) {
    return this.#http.post(`/api/tpo-signup-configurations/${id}/decisioning/evaluate`, body);
  }

  async getTpoSignupConfigurationHistory({ id, cursor = null }) {
    return this.#http.get(`/api/tpo-signup-configurations/${id}/history`, {
      params: cursor ? { cursor } : {},
    });
  }

  async getTpoSignupConfigurationHistorySnapshot({ id, historyId }) {
    return this.#http.get(`/api/tpo-signup-configurations/${id}/history/${historyId}`);
  }

  async restoreTpoSignupConfigurationHistory({ id, historyId, dryRun = false }) {
    return this.#http.post(
      `/api/tpo-signup-configurations/${id}/history/${historyId}/restore`,
      undefined,
      { ...changeSourceConfig, params: { dryRun } },
    );
  }

  async pinTpoSignupConfigurationHistory({ id, historyId }) {
    return this.#http.post(
      `/api/tpo-signup-configurations/${id}/history/${historyId}/pin`,
      undefined,
      changeSourceConfig,
    );
  }

  async unpinTpoSignupConfigurationHistory({ id, historyId }) {
    return this.#http.delete(
      `/api/tpo-signup-configurations/${id}/history/${historyId}/pin`,
      changeSourceConfig,
    );
  }

  async createTpoSignupConfiguration(body) {
    return this.#http.post('/api/tpo-signup-configurations', body, changeSourceConfig);
  }

  async updateTpoSignupConfiguration({ id, ...body }) {
    return this.#http.put(`/api/tpo-signup-configurations/${id}`, body, changeSourceConfig);
  }

  async deleteTpoSignupConfiguration({ id }) {
    return this.#http.delete(`/api/tpo-signup-configurations/${id}`, changeSourceConfig);
  }

  async uploadTpoSignupConfigurationImage({ id, type, formData }) {
    return this.#http.post(`/api/tpo-signup-configurations/${id}/images/${type}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async getTpoSignupConfigurationDomains({ id }) {
    return this.#http.get(`/api/tpo-signup-configurations/${id}/domains`);
  }

  async assignTpoSignupConfigurationDomain({ id, domainName }) {
    return this.#http.post(`/api/tpo-signup-configurations/${id}/domains`, { domainName });
  }

  async removeTpoSignupConfigurationDomain({ id, domainName }) {
    return this.#http.delete(`/api/tpo-signup-configurations/${id}/domains/${domainName}`);
  }

  async setTpoSignupConfigurationDefault({ id }) {
    return this.#http.put(
      `/api/tpo-signup-configurations/${id}/set-default`,
      undefined,
      changeSourceConfig,
    );
  }

  async setDomainDefault({ id, domainName }) {
    return this.#http.put(`/api/tpo-signup-configurations/${id}/domains/${domainName}/set-default`);
  }

  async getFormBundleCriteria({ id, config }) {
    return this.#http.get(`/api/tpo-signup-configurations/${id}/form-bundle-criteria`, config);
  }

  async createFormBundleCriteria({ id, body }) {
    return this.#http.post(`/api/tpo-signup-configurations/${id}/form-bundle-criteria`, body);
  }

  async updateFormBundleCriteria({ id, criteriaId, body }) {
    return this.#http.put(
      `/api/tpo-signup-configurations/${id}/form-bundle-criteria/${criteriaId}`,
      body,
    );
  }

  async deleteFormBundleCriteria({ id, criteriaId }) {
    return this.#http.delete(
      `/api/tpo-signup-configurations/${id}/form-bundle-criteria/${criteriaId}`,
    );
  }

  async getStepRegistry() {
    return this.#http.get('/api/tpo-signup/step-registry');
  }

  async getStepConditionExpressionDefinition(config) {
    return this.#http.get('/api/tpo-signup/expression-definition', config);
  }

  async savePreview(body) {
    return this.#http.post('/api/tpo-signup-configuration-previews', body);
  }

  async previewDocument({ id, formData, params }) {
    return this.#http.post(`/api/tpo-signup-configurations/${id}/preview-documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params,
    });
  }
}
